import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { BaseError } from "@/domains/errors";
import { atlasAssetTokenModule } from "@/modules/atlas-asset-token";

export interface AssetTokenResult {
  token: string;
  expiresAt: string | null;
}

export interface AssetAccessTokenContextType {
  token: string | null;
  expiresAt: string | null;
  isLoading: boolean;
  ensureToken: () => Promise<AssetTokenResult | null>;
  clearToken: () => Promise<void>;
}

const AtlasAssetTokenContext = createContext<AssetAccessTokenContextType | null>(null);

interface AtlasAssetTokenProviderProps {
  children: React.ReactNode;
}

const REFRESH_INTERVAL_MS = 4 * 60 * 60 * 1000; // 4 hours
const REFRESH_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 hours before expiry

export const AtlasAssetTokenProvider: React.FC<AtlasAssetTokenProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const inflightRequestRef = useRef<Promise<AssetTokenResult | null> | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isTokenExpired = useCallback((expiresAtIso: string | null): boolean => {
    if (!expiresAtIso) return false;
    const expiresAtDate = new Date(expiresAtIso);
    return expiresAtDate <= new Date();
  }, []);

  const isTokenExpiringSoon = useCallback((expiresAtIso: string | null): boolean => {
    if (!expiresAtIso) return false;
    const expiresAtDate = new Date(expiresAtIso);
    const thresholdDate = new Date(Date.now() + REFRESH_THRESHOLD_MS);
    return expiresAtDate <= thresholdDate;
  }, []);

  const fetchToken = useCallback(
    async (force = false): Promise<AssetTokenResult | null> => {
      // If we have a valid token and not forcing, return it
      if (!force && token && !isTokenExpired(expiresAt)) {
        return { token, expiresAt };
      }

      // If there's already an inflight request, wait for it to complete first
      // This prevents race conditions where a forced refresh could be overwritten
      // by an earlier non-forced request completing later
      if (inflightRequestRef.current) {
        const existingResult = await inflightRequestRef.current;
        if (!force && existingResult) {
          return existingResult;
        }
        // If force=true, proceed to fetch a fresh token
      }

      const request = (async (): Promise<AssetTokenResult | null> => {
        setIsLoading(true);

        try {
          const response = await api.post("/v2/assets/access-tokens", {});

          if (response.error) {
            throw new BaseError({
              message: "[AtlasAssetToken] Failed to generate asset access token.",
              info: objectModule.safeAsJson(response),
            });
          }

          const responseBody = response.data;
          if (!responseBody) {
            throw new BaseError({
              message: "[AtlasAssetToken] Missing response body while generating token.",
            });
          }

          const newToken = responseBody.asset_access_token;
          if (!newToken) {
            throw new BaseError({
              message: "[AtlasAssetToken] asset_access_token missing from response.",
              info: objectModule.safeAsJson({ responseBody }),
            });
          }

          const newExpiresAt = responseBody.expires_at ?? null;

          setToken(newToken);
          setExpiresAt(newExpiresAt);

          await atlasAssetTokenModule.setToken({
            info: { token: newToken, expiresAt: newExpiresAt },
          });

          return { token: newToken, expiresAt: newExpiresAt };
        } catch (error) {
          logger.error({
            message: "[AtlasAssetToken] fetch failed",
            info: { err: objectModule.safeErrorAsJson(error as BaseError) },
          });
          return null;
        } finally {
          setIsLoading(false);
          inflightRequestRef.current = null;
        }
      })();

      inflightRequestRef.current = request;
      return request;
    },
    [token, expiresAt, isTokenExpired]
  );

  const ensureToken = useCallback(async (): Promise<AssetTokenResult | null> => {
    // First check in-memory state
    if (token && !isTokenExpired(expiresAt)) {
      return { token, expiresAt };
    }

    // Then check cache
    try {
      const cached = await atlasAssetTokenModule.getToken();
      if (cached && !isTokenExpired(cached.expiresAt)) {
        setToken(cached.token);
        setExpiresAt(cached.expiresAt);
        return { token: cached.token, expiresAt: cached.expiresAt };
      }
    } catch (error) {
      logger.warn({
        message: "[AtlasAssetToken] Failed to read from cache",
        info: { err: objectModule.safeErrorAsJson(error as BaseError) },
      });
    }

    // Fetch new token
    return fetchToken();
  }, [token, expiresAt, isTokenExpired, fetchToken]);

  const clearToken = useCallback(async (): Promise<void> => {
    setToken(null);
    setExpiresAt(null);
    await atlasAssetTokenModule.clearToken();
  }, []);

  // Hydrate from cache on mount
  useEffect(() => {
    const hydrate = async () => {
      try {
        const cached = await atlasAssetTokenModule.getToken();
        if (cached && !isTokenExpired(cached.expiresAt)) {
          setToken(cached.token);
          setExpiresAt(cached.expiresAt);
        }
      } catch (error) {
        logger.warn({
          message: "[AtlasAssetToken] Failed to hydrate from cache",
          info: { err: objectModule.safeErrorAsJson(error as BaseError) },
        });
      }
    };

    hydrate();
  }, [isTokenExpired]);

  // Set up refresh interval
  useEffect(() => {
    const refreshIfExpiringSoon = async () => {
      if (!token || !expiresAt) return;

      if (isTokenExpiringSoon(expiresAt)) {
        await fetchToken(true);
      }
    };

    refreshIntervalRef.current = setInterval(refreshIfExpiringSoon, REFRESH_INTERVAL_MS);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [token, expiresAt, isTokenExpiringSoon, fetchToken]);

  const value: AssetAccessTokenContextType = {
    token,
    expiresAt,
    isLoading,
    ensureToken,
    clearToken,
  };

  return (
    <AtlasAssetTokenContext.Provider value={value}>{children}</AtlasAssetTokenContext.Provider>
  );
};

export const useAtlasAssetToken = (): AssetAccessTokenContextType => {
  const context = useContext(AtlasAssetTokenContext);

  if (context === null) {
    throw new Error("useAtlasAssetToken must be used within an AtlasAssetTokenProvider");
  }

  return context;
};
